"use client";

import React from "react";
import { Leaf, Flower2, Droplets, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const experiences = [{
  id: "yoga",
  title: "4 Day Escape",
  description: "A curated four-day journey through Colombia’s Caribbean coast blending boutique beachfront hospitality, jungle adventure, and barefoot luxury. From Tayrona’s tropical trails to slow river drifts into the sea, this itinerary is designed for travelers seeking both immersion and restoration.",
  image: {
    src: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/20230808_133902-scaled.jpg?v=151duymezed",
    srcset: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/20230808_133902-scaled.jpg?v=tof4o8r3tcn-wvc-srcset",
    sizes: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/20230808_133902-scaled.jpg?v=tf12h9h20vh-wvc-sizes",
    alt: "Woman practicing yoga in a serene setting"
  },
  icon: Leaf,
  link: "/CurationPage"
}, {
  id: "meditation",
  title: "6 Day Escape",
  description: "An extended coastal escape designed around slower rhythms, meaningful exploration, and restorative luxury along the Caribbean coast of Palomino. Combining Tayrona adventure, jungle river experiences, beachside recovery days, and boutique hospitality at Makao, this itinerary invites travelers to fully settle into the atmosphere of northern Colombia.",
  image: {
    src: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/B7D6FA84-747F-4DDA-95F3-AC4F012AD751-scaled.jpg?v=5pgqs0kr6av",
    srcset: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/B7D6FA84-747F-4DDA-95F3-AC4F012AD751-scaled.jpg?v=8umti1s27k9-wvc-srcset",
    sizes: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/B7D6FA84-747F-4DDA-95F3-AC4F012AD751-scaled.jpg?v=6wjev6m862-wvc-sizes",
    alt: "Person meditating peacefully"
  },
  icon: Flower2,
  link: "/CurationPage"
}];
export default function HolisticWellness() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="holisticwellness" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-wide">The Makao Experience</h2>
          <Separator className="w-24 mx-auto bg-secondary/40 h-[2px] mb-8" />
          <p className="text-lg md:text-xl text-foreground font-sans leading-relaxed">Palomino is where the Sierra Nevada mountains meet the Caribbean Sea, creating one of Colombia’s most immersive coastal experiences. Gain access to curated adventures, wellness experiences, and trusted local guides designed around the rhythm of the region.</p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {experiences.map((experience, index) => {
          const Icon = experience.icon;
          return <div key={experience.id} data-index={index} className={`transition-all duration-1000 ease-out delay-${index * 200} ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="h-full bg-card border border-border/50 shadow-md hover:shadow-lg transition-all duration-500 group overflow-hidden rounded-none">
                  <div className="overflow-hidden">
                    <AspectRatio ratio={4 / 3}>
                      <img src={experience.image.src} data-wvc-srcset={experience.image.srcset} data-wvc-sizes={experience.image.sizes} alt={experience.image.alt} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                    </AspectRatio>
                  </div>
                  
                  <CardHeader className="relative pt-8 pb-4 px-8">
                    
                    <CardTitle className="font-default text-2xl text-foreground tracking-wide">
                      {experience.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="px-8 pb-8 flex flex-col flex-grow justify-between">
                    <CardDescription className="text-base text-muted-foreground leading-relaxed mb-8 font-sans">
                      {experience.description}
                    </CardDescription>
                    
                    <Button asChild variant="link" className="p-0 h-auto text-primary hover:text-secondary justify-start font-sans tracking-wide uppercase text-sm group/btn">
                      <Link to={experience.link}>
                        Discover Experience
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

      </div>
    </section>;
}